import {testIdProps} from '@github-ui/test-id-props'
import {LinkExternalIcon, PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {use, useCallback, useState} from 'react'

import type {IssueTitleWithContentType} from '../../../api/columns/contracts/title'
import type {MissingSubIssue} from '../../../api/common-contracts'
import {ItemType} from '../../../api/memex-items/item-type'
import {FocusType} from '../../../navigation/types'
import {TextPlaceholder} from '../../common/placeholders'
import {SanitizedHtml} from '../../dom/sanitized-html'
import {ItemStateForTitle} from '../../item-state'
import {useBulkAddItems} from '../../side-panel/bulk-add/bulk-add-items-provider'
import {BaseCell} from '../cells/base-cell'
import {moveTableFocus, useStableTableNavigation} from '../navigation'
import {FocusCellContext} from '../use-table-focus-cell'
import styles from './title-renderer.module.css'

export const LoadingSubIssuesCell = (subIssueDepth: number) => (
  <BaseCell style={{'--hierarchy-depth': subIssueDepth, '--missing': 1}} className={styles.hierarchyDepth}>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

export const MissingSubIssuesRenderer = ({
  missingSubIssues,
  subIssueDepth,
}: {
  missingSubIssues: Array<MissingSubIssue>
  subIssueDepth: number
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const {addSelectedItemsRequest, addingItems} = useBulkAddItems()
  const {navigationDispatch} = useStableTableNavigation()
  const {focusCellElement, wrapperRef, childRef} = use(FocusCellContext)

  const countMissingSubIssues = missingSubIssues.length
  const missingSubIssuesText = `${countMissingSubIssues} sub-issue${
    countMissingSubIssues !== 1 ? 's' : ''
  } not in this project`

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        navigationDispatch(moveTableFocus({focusType: FocusType.Suspended}))
      } else {
        navigationDispatch(moveTableFocus({focusType: FocusType.Focus}))
        focusCellElement()
      }
      setMenuOpen(open)
    },
    [navigationDispatch, focusCellElement],
  )

  const addMissingSubIssues = useCallback(async () => {
    const itemsToAdd = []
    for (const subIssue of missingSubIssues) {
      const {owner, repositoryName, number} = subIssue
      itemsToAdd.push({
        userName: owner,
        repoName: repositoryName,
        number,
        hasSubIssues: false,
      })
    }

    if (itemsToAdd.length === 0) {
      return
    }
    await addSelectedItemsRequest.perform(itemsToAdd)
  }, [addSelectedItemsRequest, missingSubIssues])

  return (
    <BaseCell
      style={{'--hierarchy-depth': subIssueDepth, '--missing': 1}}
      className={clsx(styles.missingSubIssuesText, styles.hierarchyDepth)}
    >
      {missingSubIssuesText}
      <ActionMenu open={menuOpen} onOpenChange={onOpenChange} anchorRef={childRef}>
        <ActionMenu.Anchor>
          <IconButton
            className={styles.missingSubIssuesButton}
            aria-label="Manage missing sub-issues"
            variant="invisible"
            size="small"
            icon={TriangleDownIcon}
            loading={addingItems}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay onEscape={() => onOpenChange(false)} returnFocusRef={wrapperRef} width="medium">
          <ActionList>
            <ActionList.Item onSelect={addMissingSubIssues}>
              <ActionList.LeadingVisual>
                <PlusIcon />
              </ActionList.LeadingVisual>
              Add all to project
            </ActionList.Item>
            <ActionList.Divider />
            {missingSubIssues.map(subIssue => (
              <MissingSubIssueItem key={subIssue.id} subIssue={subIssue} />
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </BaseCell>
  )
}

const MissingSubIssueItem = ({subIssue}: {subIssue: MissingSubIssue}) => {
  const {number, state, stateReason, id, title, titleHtml, url} = subIssue
  const titleWithType: IssueTitleWithContentType = {
    contentType: ItemType.Issue,
    value: {
      number,
      state,
      stateReason,
      issueId: id,
      title: {
        raw: title,
        html: titleHtml,
      },
    },
  }
  return (
    <ActionList.LinkItem
      key={id}
      className={styles.missingSubIssueItem}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ActionList.LeadingVisual>
        <ItemStateForTitle title={titleWithType} isBlocked={false} />
      </ActionList.LeadingVisual>
      <SanitizedHtml>{titleHtml}</SanitizedHtml>
      <span className={styles.missingSubIssueText}>#{number}</span>
      <ActionList.TrailingVisual>
        <LinkExternalIcon />
      </ActionList.TrailingVisual>
    </ActionList.LinkItem>
  )
}
